import {
  AiPricingTableSchema,
  globalScope,
  type AiModelPricing,
  type AiPricingTable,
  type SettingDefinitionMetadata,
} from '@aysar/contracts';
import { useCallback, useMemo } from 'react';

import { useHasPermission } from '@/shared/hooks/useHasPermission/useHasPermission';
import {
  useClearSettingMutation,
  useSettingsDetailsQuery,
  useSettingsRegistryQuery,
  useWriteSettingMutation,
} from '@/shared/services/settings/settings.api';
import { toApiError } from '@/shared/utils/transform-error/transform-error';
import { type ApiError } from '@/shared/utils/transform-error/transform-error.types';

/** The one key this hook is about. */
export const AI_PRICING_KEY = 'ai.pricing';

/**
 * What the card can say about the rate table.
 *
 * `restricted` is a first-class answer rather than an error, for the reason
 * `useProviderActors` gives: `settings.read` is a **different** permission from
 * the `integrations.read` that opens this screen, and an operator holding one
 * without the other must be told so — never shown a failed request they cannot
 * act on.
 */
export type AiPricingState = 'restricted' | 'loading' | 'error' | 'ready';

export interface UseAiPricing {
  state: AiPricingState;
  error: ApiError | null;
  refetch: () => void;
  /** The coded defaults, from the registry — what a reset restores. */
  codedDefault: AiPricingTable;
  /** The stored GLOBAL override only — its keys are what a reset can remove. */
  stored: AiPricingTable;
  /** What the platform resolves to right now. */
  effective: AiPricingTable;
  /** The definition, so the editor can honour `dangerous` rather than assume it. */
  definition: SettingDefinitionMetadata | null;
  /** `settings.manage` — the key the api guards the write with, not this screen's. */
  mayManage: boolean;
  /** Writes one key's rates, merged over whatever the server currently holds. */
  saveRate: (key: string, rates: AiModelPricing) => Promise<void>;
  /**
   * Removes one key from the override. Clears the whole row when it was the last
   * one, so the source visibly returns to the coded default.
   */
  removeRate: (key: string) => Promise<void>;
  isSaving: boolean;
}

const EMPTY: AiPricingTable = {};

/**
 * `ai.pricing`, read and written from the module that owns its editor (P10.4).
 *
 * Three facts, from two requests: the **coded default** comes from the registry
 * (a definition's `defaultValue`), the **stored override** and the **effective**
 * value from the batch details endpoint. All three are needed and none is
 * derivable from the others — `effective` is `{...coded, ...stored}` by the api's
 * per-field merge, so an override cannot be recovered from it, and the coded
 * default cannot be recovered from either.
 *
 * **Last write wins, and that is recorded rather than hidden.** `PUT
 * /admin/settings/:key` replaces the scope's whole value, so two operators
 * editing two different rows at the same moment will have one overwrite the
 * other. The write below is composed from the freshest value the cache holds
 * (the mutation invalidates it, so it is refetched after every save), which
 * narrows the window to one round trip — it does not close it. The real fix is a
 * precondition stamp (`expectedUpdatedAt`) on the settings write, which is a
 * settings-wide backend concern touching every key and must not be invented
 * inside a rate table.
 */
export function useAiPricing(): UseAiPricing {
  const { can } = useHasPermission();
  const mayRead = can('settings.read');
  const mayManage = can('settings.manage');

  // Skipped, never fired-and-refused: a request guaranteed to 403 costs a round
  // trip, logs an error and teaches the operator nothing (`useProviderActors`).
  const registry = useSettingsRegistryQuery(undefined, { skip: !mayRead });
  const details = useSettingsDetailsQuery({ keys: AI_PRICING_KEY }, { skip: !mayRead });

  const [writeSetting, writeState] = useWriteSettingMutation();
  const [clearSetting, clearState] = useClearSettingMutation();

  const definition = useMemo(
    () => findDefinition(registry.data?.groups ?? []),
    [registry.data?.groups],
  );

  const detail = details.data?.items.find((item) => item.key === AI_PRICING_KEY);

  const codedDefault = useMemo(() => tableOf(definition?.defaultValue), [definition?.defaultValue]);

  const stored = useMemo(
    () => tableOf(detail?.values.find((row) => row.scopeType === 'global')?.value),
    [detail?.values],
  );

  const effective = useMemo(
    () => tableOf(detail?.effective.find((row) => row.scopeType === 'global')?.value),
    [detail?.effective],
  );

  const saveRate = useCallback(
    async (key: string, rates: AiModelPricing) => {
      try {
        await writeSetting({
          key: AI_PRICING_KEY,
          // The whole table, because the api replaces a scope's value outright.
          // Composed from `stored` rather than from `effective`: writing the
          // merged table would freeze today's coded defaults into the database,
          // and a later release correcting a rate the operator never touched
          // would then have no effect at all.
          body: { ...globalScope(), value: { ...stored, [key]: rates } },
        }).unwrap();
      } catch (error) {
        throw toApiError(error);
      }
    },
    [stored, writeSetting],
  );

  const removeRate = useCallback(
    async (key: string) => {
      const next = { ...stored };
      delete next[key];

      try {
        if (Object.keys(next).length === 0) {
          // The last override: CLEAR the row rather than storing `{}`, so the
          // screen says "default" again instead of "overridden with nothing".
          await clearSetting({ key: AI_PRICING_KEY, body: globalScope() }).unwrap();
          return;
        }

        await writeSetting({
          key: AI_PRICING_KEY,
          body: { ...globalScope(), value: next },
        }).unwrap();
      } catch (error) {
        throw toApiError(error);
      }
    },
    [clearSetting, stored, writeSetting],
  );

  const loaded = !registry.isLoading && !details.isLoading;

  const requestError =
    registry.error !== undefined
      ? toApiError(registry.error)
      : details.error === undefined
        ? null
        : toApiError(details.error);

  /**
   * A server that declares no `ai.pricing` is an error with an ERROR OBJECT.
   *
   * It used to be a `state` of `'error'` beside a `null` error, and the card
   * hands `error` to `RenderGate` — which renders its children when there is
   * none. The tables then drew with `codedDefault = {}`, and two things quietly
   * changed meaning: every overridden row's verb flipped from "restore the
   * shipped rate" to the destructive "remove this rate" (there is no coded
   * default to restore), and the editor skipped the `dangerous` confirmation
   * (there is no definition to read it from). A state one spec asserted and the
   * surface could not show.
   */
  const missingDefinition: ApiError | null =
    mayRead && loaded && requestError === null && definition === null
      ? { status: 0, code: 'setting_not_declared', message: AI_PRICING_KEY }
      : null;

  const error = requestError ?? missingDefinition;

  const state: AiPricingState = !mayRead
    ? 'restricted'
    : !loaded
      ? 'loading'
      : error !== null
        ? 'error'
        : 'ready';

  return {
    state,
    error,
    refetch: () => {
      void registry.refetch();
      void details.refetch();
    },
    codedDefault,
    stored,
    effective,
    definition,
    mayManage,
    saveRate,
    removeRate,
    isSaving: writeState.isLoading || clearState.isLoading,
  };
}

/** The `ai.pricing` definition, wherever the registry happens to group it. */
function findDefinition(groups: SettingsRegistryGroups): SettingDefinitionMetadata | null {
  for (const group of groups) {
    for (const subgroup of group.subgroups) {
      const found = subgroup.settings.find((setting) => setting.key === AI_PRICING_KEY);
      if (found !== undefined) return found;
    }
  }

  return null;
}

type SettingsRegistryGroups = readonly {
  subgroups: readonly { settings: readonly SettingDefinitionMetadata[] }[];
}[];

/**
 * A wire value narrowed to a rate table — `{}` when it is anything else.
 *
 * Parsed rather than cast: `value` is `unknown` on the wire by necessity (only
 * the api holds the registry), and this is the boundary U4 permits `unknown` at,
 * on condition that it is narrowed here rather than carried inwards. A payload
 * that fails is an empty table, not a crash: an older server, or a value edited
 * by hand, must not blank the whole Integrations page.
 */
function tableOf(value: unknown): AiPricingTable {
  const parsed = AiPricingTableSchema.safeParse(value);

  return parsed.success ? parsed.data : EMPTY;
}
